<template>
  <div class="customer__form modal modal--default">
    <div class="modal__panel modal__panel--search-potential">
      <div class="modal__logo-wrapper">
        <div class="modal__logo"></div>
      </div>

      <div class="modal__main-icon modal__main-icon--potential"></div>
      <div v-if="!showSearch" class="modal__back" @click="showSearch = true"></div>
      <div class="modal__close" @click="store.commit('setShowSearchPotentialCustomerModal', false)"></div>

      <div class="form">
        <div class="navigators">
          <div class="navigator navigator--active">
            <div class="navigator__bullet"></div>
            <div class="navigator__title">{{ $t(`searchPotentialCustomers.${"title"}`) }}</div>
          </div>
        </div>
        <div class="add__form">
          <transition name="fade" mode="out-in">
            <Loading v-if="formLoading" />
            <div v-else class="add__content">
              <transition name="fade" mode="out-in">
                <div v-if="showSearch" class="" key="0">
                  <div class="form__label">{{ $t(`searchPotentialCustomers.${"phoneLabel"}`) }}</div>
                  <input type="text" class="input input--default" :class="{ 'input--error': phoneKeywordsHasError }"
                    v-model="phoneKeywords" @input="phoneKeywordsHasError = false" />
                  <button class="modal__button modal__button--submit button button--default"
                    :disabled="!canSearchPotentialCustomer" @click="searchPotentialCustomer()">
                    {{ $t(`searchPotentialCustomers.${"searchBtn"}`) }}
                  </button>
                </div>

                <div v-else class="results" key="1">
                  <div v-if="potentialCustomers">
                    <div v-for="potentialCustomer in potentialCustomers" :key="potentialCustomer.id"
                      class="contact contact--exists">
                      <div v-if="potentialCustomer" @click="selectCustomer(potentialCustomer, $event)"
                        class="contact__name">
                        <span v-if="potentialCustomer.full_name">
                          {{ potentialCustomer.full_name }}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div v-else class="contact">
                    {{ $t(`searchPotentialCustomers.${"noResultsMsg"}`) }}

                    <button type="button" class="button button--add button--default" @click="showAddCustomerModal()"
                      v-if="canCreateCustomer">
                      {{ $t(`dashboard.statistics.${"addCustomerBtn"}`) }}
                    </button>
                  </div>
                </div>
              </transition>
            </div>
          </transition>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useStore } from "vuex";
import api from "@/api";
import Loading from "@/partials/Loading";
import { extractEntityTeamIds } from "@/helpers/teamAuthorization";

import { useRouter } from "vue-router";
defineOptions({
  name: "SearchPotentialCustomerModal"
});

const store = useStore();
const router = useRouter();

// Reactive State
const showSearch = ref(true);
const formLoading = ref(false);
const phoneKeywords = ref("");
const phoneKeywordsHasError = ref(false);
const potentialCustomers = ref(null);
const isSuperAdmin = computed(() => store.getters.isSuperAdmin);
const canOpenSearchPotentialCustomerModal = computed(() => {
  if (isSuperAdmin.value) return true;

  const accessTeams = store.getters.authorizedTeamIds("customer_access");
  const createTeams = store.getters.authorizedTeamIds("customer_create");

  const hasAccess = accessTeams === null || (Array.isArray(accessTeams) && accessTeams.length > 0);
  const hasCreateAccess = createTeams === null || (Array.isArray(createTeams) && createTeams.length > 0);

  return hasAccess && hasCreateAccess;
});
const canCreateCustomer = computed(() => {
  if (isSuperAdmin.value) return true;

  const authorizedIds = store.getters.authorizedTeamIds("customer_create");
  return authorizedIds === null || (Array.isArray(authorizedIds) && authorizedIds.length > 0);
});
const canSearchPotentialCustomer = computed(() => {
  if (isSuperAdmin.value) return true;

  const authorizedIds = store.getters.authorizedTeamIds("customer_create");
  return authorizedIds === null || (Array.isArray(authorizedIds) && authorizedIds.length > 0);
});

onMounted(() => {
  if (!canOpenSearchPotentialCustomerModal.value) {
    store.commit("setShowSearchPotentialCustomerModal", false);
  }
});

// Methods
const searchPotentialCustomer = async () => {
  if (!canSearchPotentialCustomer.value) {
    store.commit("setShowSearchPotentialCustomerModal", false);
    return;
  }

  if (phoneKeywords.value) {
    try {
      formLoading.value = true;
      const { data } = await api({ requiresAuth: true }).get(
        `/v1/filter/potential-customers?phone=${phoneKeywords.value}`
      );

      potentialCustomers.value = data.data || null;
      showSearch.value = false;
    } catch (err) {
      console.error(err);
    } finally {
      formLoading.value = false;
    }
  } else {
    phoneKeywordsHasError.value = true;
  }
};


const showAddCustomerModal = () => {
  if (!canCreateCustomer.value) {
    return;
  }

  store.commit("setShowSearchPotentialCustomerModal", false);
  store.commit("setShowAddCustomerModal", true);
  store.commit("setIsAddingCustomer", true);
};

// const selectCustomer = (customer) => {
//   if (!canSearchPotentialCustomer.value) {
//     store.commit("setShowSearchPotentialCustomerModal", false);
//     return;
//   }

//   store.commit("setPotentialCustomer", customer);
//   store.commit("setShowSearchPotentialCustomerModal", false);
// };


const canShowCustomer = (customer) => {
  if (isSuperAdmin.value) return true;

  const teamIds = extractEntityTeamIds(customer ?? {});
  return teamIds.length > 0
    ? store.getters.canPerformActionOnEntity("customer_show", teamIds)
    : false;
};

const selectCustomer = (customer, event) => {
  if (!canShowCustomer(customer)) {
    return;
  }

  const params = { entity: "potential-customer", id: customer.id };
  const query = { tab: 5 };
  const route = router.resolve({ name: "AccountDetails", params, query });

  if (event?.ctrlKey || event?.metaKey) {
    window.open(route.href, "_blank");
  } else {
    router.push({ name: "AccountDetails", params, query });
  }

  store.commit("setShowSearchPotentialCustomerModal", false);
};

</script>

<style scoped lang="scss">
.add__content {
  padding-top: 30px !important;

  .form {
    &__label {
      color: var(--text-2);
      text-align: center;
      margin-bottom: 20px;
      font-size: var(--x-small);
      font-family: var(--muli-text-semi-bold);
    }


  }
}

.button {
  left: 50%;
  transform: translateX(-50%);
  bottom: 150px;
}

.modal__button {
  position: absolute;
  bottom: 40px;
  height: 40px;
  transition: all 0.3s ease-in-out;
}

.modal__button--submit {
  width: 135px;
  box-shadow: 0px 7px 18px var(--shadow-2);
  right: 100px;
}

.modal__button--submit:hover {
  background-color: var(--hover-1);
  box-shadow: 0px -7px 18px var(--shadow-2);
}

.modal__button--cancel {
  background-color: var(--bg-12);
  box-shadow: 0px 7px 18px var(--shadow-8);
  right: 260px;
}

.modal__button--cancel:hover {
  background-color: var(--bg-12);
  box-shadow: 0px -7px 18px var(--shadow-8);
}

.button--add {
  position: relative;
  transform: initial;
  left: 0;
  bottom: 0;
  margin: 0 auto;
  margin-top: 50px;
}

.contact {
  box-shadow: 0px 3px 6px var(--shadow-3);
  padding: 25px;
  text-align: center;
  border-radius: var(--radius-1);
  position: relative;
  margin-bottom: 20px;
  transition: all 0.3s ease;

  &--exists {
    cursor: pointer;

    &:hover {
      background: var(--bg-6);
      box-shadow: none;
    }
  }

  &__thumbnail {
    position: absolute;
    height: 46px;
    width: 46px;
    background: var(--bg-21);
    border-radius: 50%;
    top: 10px;
    left: 20px;
  }

  &__name {
    color: var(--text-9);
  }

  &__address {
    margin-bottom: 6px;
  }

  &__address,
  &__city {
    color: var(--text-2);
    font-size: var(--x-small);
  }

  &__empty {
    height: 26px;
    width: 116px;
    background: var(--bg-21);
    border-radius: var(--radius-1);

    &--large {
      width: 143px;
    }
  }
}
</style>
